import { useEffect, useCallback } from 'react';
import { create } from 'zustand';
import { clearSessionTokenCache, computeIsPremium, dev } from '@breath-lab/domain';
import {
  getServices,
  type PurchaseCustomerInfo,
  type PurchaseOffering,
  type PurchasePackage,
} from '@breath-lab/services';
import { useAuth, bindRevenueCat, getRcBoundUid, isRcBindingEnabled, _registerOnRcBound } from './useAuth';

const PREMIUM_ENTITLEMENT = 'premium';

/** Single source of truth for "this CustomerInfo carries the premium entitlement". */
export function rcPremiumActive(info: PurchaseCustomerInfo | null): boolean {
  return info?.entitlements.active[PREMIUM_ENTITLEMENT] != null;
}

/** True iff this CustomerInfo's active premium entitlement is a free trial. */
export function isPremiumTrial(info: PurchaseCustomerInfo | null): boolean {
  return info?.entitlements.active[PREMIUM_ENTITLEMENT]?.periodType === 'trial';
}

/**
 * Thrown by purchasePackage/restorePurchases when the RC identity could not
 * be bound to the signed-in Firebase uid. The transaction is REFUSED — a
 * purchase or restore attributed to an anonymous RC user produces an orphan
 * entitlement the webhook (rightly) discards. Screens map this to dedicated
 * copy (upgrade:errors.accountNotVerified), not the generic payment error.
 */
export class PurchaseIdentityError extends Error {
  readonly code = 'identity_unverified';
  constructor() {
    super('RevenueCat identity could not be bound to the signed-in user');
    this.name = 'PurchaseIdentityError';
  }
}

/**
 * Thrown by restorePurchases when the restore call itself fails (network, RC
 * outage, store error) — as opposed to succeeding and simply finding no prior
 * purchase. Screens must map this to a "try again" message, never to "no
 * previous purchase found", which would wrongly tell a paying user they never
 * bought anything.
 */
export class RestoreFailedError extends Error {
  readonly code = 'restore_failed';
  constructor() {
    super('RevenueCat restore failed');
    this.name = 'RestoreFailedError';
  }
}

// Type guard that survives the package boundary: if two copies of this module
// are ever bundled, `instanceof` across them fails, so fall back to the stable
// `code` discriminant. Screens should use this, not a bare `instanceof`.
export function isPurchaseIdentityError(err: unknown): boolean {
  return (
    err instanceof PurchaseIdentityError ||
    (typeof err === 'object' && err !== null && (err as { code?: string }).code === 'identity_unverified')
  );
}

// Hard gate for money-moving calls: re-bind (idempotent for RC) and refuse
// the transaction when unbound. Login is allowed to proceed without RC
// (degraded mode) — transactions are not.
async function ensureBoundForTransaction(): Promise<void> {
  const uid = useAuth.getState().user?.uid;
  if (!uid) throw new PurchaseIdentityError();
  const bound = await bindRevenueCat(uid);
  if (!bound) throw new PurchaseIdentityError();
}

// Hydration guard: customer info fetched while RC is bound to a different
// (or anonymous) identity must never land in this user's store. Web RC has
// no logOut, so after A signs out and B's bind fails, the SDK still answers
// as A — without this check B would render A's premium.
function rcIdentityMatches(uid: string): boolean {
  return !isRcBindingEnabled() || getRcBoundUid() === uid;
}

// Both money-moving calls are identity-gated: an anonymous purchase or
// restore creates an entitlement the webhook discards — the user pays and
// receives nothing in Firestore. Module-level (not hook-bound) so the gate
// is unit-testable alongside the other store functions.
export async function purchaseGated(pkg: PurchasePackage): Promise<PurchaseCustomerInfo> {
  await ensureBoundForTransaction();
  const info = await getServices().purchases.purchasePackage(pkg);
  onCustomerInfoUpdate(info);
  return info;
}

export async function restoreGated(): Promise<PurchaseCustomerInfo> {
  await ensureBoundForTransaction();
  try {
    const info = await getServices().purchases.restorePurchases();
    onCustomerInfoUpdate(info);
    return info;
  } catch (err) {
    // A failed restore is NOT the same as "no purchase to restore" — surface
    // it distinctly so the screen shows a retryable error, not the misleading
    // "no previous purchase found". A successful restore that simply has no
    // premium returns normally (info without an active entitlement).
    dev.warn('[useSubscription] restorePurchases failed:', err);
    throw new RestoreFailedError();
  }
}

// ---------------------------------------------------------------------------
// Shared Zustand store — single source of truth for subscription state.
// All useSubscription() callers read from and write to the same store, so
// refreshEntitlements() in any component instantly updates every consumer.
// ---------------------------------------------------------------------------
interface SubscriptionStore {
  offering: PurchaseOffering | null;
  customerInfo: PurchaseCustomerInfo | null;
  isLoading: boolean;
  setOffering: (o: PurchaseOffering | null) => void;
  setCustomerInfo: (c: PurchaseCustomerInfo | null) => void;
  setIsLoading: (v: boolean) => void;
}

const useSubscriptionStore = create<SubscriptionStore>((set) => ({
  offering: null,
  customerInfo: null,
  isLoading: true,
  setOffering: (offering) => set({ offering }),
  setCustomerInfo: (customerInfo) => set({ customerInfo }),
  setIsLoading: (isLoading) => set({ isLoading }),
}));

// Applies a fresh CustomerInfo snapshot to the store and evicts the session
// token cache if OVERALL premium access (RC entitlement OR accessLevel
// fallback) was just revoked. An RC flip while accessLevel stays premium is
// not a revoke — the user is still entitled.
export function onCustomerInfoUpdate(info: PurchaseCustomerInfo): void {
  const { customerInfo, setCustomerInfo } = useSubscriptionStore.getState();
  const user = useAuth.getState().user;
  // Identity guard: drop snapshots fetched for someone other than the
  // currently signed-in, currently bound user (stale bind, signed out, web
  // SDK still answering as the previous user).
  if (!user?.uid || !rcIdentityMatches(user.uid)) {
    dev.warn('[subscription] dropping customer info for unbound identity');
    return;
  }
  const accessLevel = user.accessLevel;
  const wasPremium = computeIsPremium(rcPremiumActive(customerInfo), accessLevel);
  const isNowPremium = computeIsPremium(rcPremiumActive(info), accessLevel);
  if (wasPremium && !isNowPremium) clearSessionTokenCache();
  setCustomerInfo(info);
}

// Late-bind hook: when the auth listener's RC bind completes AFTER its
// timeout, the sign-in prefetch was skipped by the identity guard — re-run
// it now so offerings + customer info hydrate (an empty paywall otherwise
// has no recovery point).
_registerOnRcBound((uid) => {
  void prefetchSubscription(uid);
});

// Mirror watcher for the other premium signal: when an accessLevel revoke
// (webhook EXPIRATION/BILLING_ISSUE) removes the user's last premium signal,
// evict the session token cache. Sign-out is handled by teardownUser().
useAuth.subscribe((state, prevState) => {
  const prevLevel = prevState.user?.accessLevel;
  const nextLevel = state.user?.accessLevel;
  if (prevLevel === nextLevel) return;
  const rcActive = rcPremiumActive(useSubscriptionStore.getState().customerInfo);
  if (computeIsPremium(rcActive, prevLevel) && !computeIsPremium(rcActive, nextLevel)) {
    clearSessionTokenCache();
  }
});

/**
 * Install a visibility-change listener that re-fetches RevenueCat customer
 * info whenever the tab regains focus. Called once from the app bootstrap so
 * a cancellation made in another tab (e.g. Stripe portal) is reflected
 * promptly. RN apps wire AppState in their own bootstrap with the same shape.
 */
export function installVisibilityRefresh(
  addListener: (cb: () => void) => void,
  isVisible: () => boolean,
): void {
  addListener(() => {
    const purchases = getServices().purchases;
    if (!isVisible() || !purchases.isConfigured()) return;
    const uid = useAuth.getState().user?.uid;
    if (!uid || !rcIdentityMatches(uid)) return;
    purchases.getCustomerInfo()
      .then(onCustomerInfoUpdate)
      .catch(() => {});
  });
}

/**
 * Clears the shared subscription store. MUST be called from sign-out teardown
 * (web teardownUser, mobile teardown): the useSubscription hook only clears
 * the store when a consumer happens to be mounted as the uid flips, and
 * prefetchSubscription early-returns on a hydrated store — without this reset
 * the next account on a shared device renders the previous account's
 * CustomerInfo and premium status.
 */
export function resetSubscriptionStore(): void {
  useSubscriptionStore.setState({ offering: null, customerInfo: null, isLoading: false });
}

/** Test-only — reset/seed the shared store between cases. Do NOT call from app code. */
export function _resetSubscriptionForTesting(seed?: {
  offering?: PurchaseOffering | null;
  customerInfo?: PurchaseCustomerInfo | null;
  isLoading?: boolean;
}): void {
  useSubscriptionStore.setState({
    offering: seed?.offering ?? null,
    customerInfo: seed?.customerInfo ?? null,
    isLoading: seed?.isLoading ?? true,
  });
}

// Pre-warm cache so pages can kick off the fetch before the panel mounts.
// Still module-level to deduplicate concurrent calls, but the result lands
// in the shared Zustand store rather than per-component state.
const _inflight = new Map<string, Promise<void>>();

export async function prefetchSubscription(uid: string): Promise<void> {
  const { offering, customerInfo, setOffering, setCustomerInfo, setIsLoading } =
    useSubscriptionStore.getState();

  // Already hydrated — nothing to do.
  if (offering != null && customerInfo != null) return;

  const existing = _inflight.get(uid);
  if (existing) return existing;
  const purchases = getServices().purchases;
  if (!purchases.isConfigured()) {
    setIsLoading(false);
    return;
  }
  // Identity guard — see rcIdentityMatches. The late-bind hook above re-runs
  // this prefetch once the bind lands.
  if (!rcIdentityMatches(uid)) {
    dev.warn('[prefetchSubscription] skipped: RC not bound to', uid);
    setIsLoading(false);
    return;
  }

  const p = Promise.allSettled([
    purchases.getOfferings(),
    purchases.getCustomerInfo(),
  ])
    .then(([offeringsResult, infoResult]) => {
      // Re-check identity AFTER the awaits: a sign-out or user switch may have
      // landed while these fetches were in flight. Writing now would
      // re-pollute a freshly cleared store with the previous identity's data
      // (the guard before the fetch only covers the start of the call).
      if (!rcIdentityMatches(uid)) {
        dev.warn('[prefetchSubscription] dropping result: identity changed during fetch');
        return;
      }
      if (offeringsResult.status === 'fulfilled') {
        const resolved = offeringsResult.value.all[purchases.primaryOfferingId] ?? offeringsResult.value.current;
        setOffering(resolved ?? null);
      } else {
        dev.warn('[prefetchSubscription] offerings failed:', offeringsResult.reason);
      }
      if (infoResult.status === 'fulfilled') {
        setCustomerInfo(infoResult.value);
      } else {
        dev.warn('[prefetchSubscription] customerInfo failed:', infoResult.reason);
      }
    })
    .finally(() => {
      _inflight.delete(uid);
      setIsLoading(false);
    });

  _inflight.set(uid, p);
  return p;
}

export function getIsPremium(): boolean {
  const { customerInfo } = useSubscriptionStore.getState();
  return computeIsPremium(rcPremiumActive(customerInfo), useAuth.getState().user?.accessLevel);
}

// Force-refresh customer info and update the store. Use this instead of
// prefetchSubscription() when you need a guaranteed fresh read (e.g. on gate hit).
export async function refreshCustomerInfo(): Promise<void> {
  const purchases = getServices().purchases;
  if (!purchases.isConfigured()) return;
  try {
    const info = await purchases.getCustomerInfo();
    onCustomerInfoUpdate(info);
  } catch (err) {
    dev.warn('[refreshCustomerInfo] failed:', err);
  }
}

export interface SubscriptionState {
  isPremium: boolean;
  isLoading: boolean;
  currentOffering: PurchaseOffering | null;
  customerInfo: PurchaseCustomerInfo | null;
  managementURL: string | null;
  refreshEntitlements: () => Promise<PurchaseCustomerInfo | null>;
  /** Identity-gated. Throws PurchaseIdentityError when RC can't be bound to the signed-in uid. */
  purchasePackage: (pkg: PurchasePackage) => Promise<PurchaseCustomerInfo>;
  /**
   * Identity-gated. Resolves with the restored customer info (which may carry
   * no active entitlement when there is genuinely nothing to restore). Throws
   * PurchaseIdentityError when unbound, or RestoreFailedError when the restore
   * call itself fails (network/store/RC outage).
   */
  restorePurchases: () => Promise<PurchaseCustomerInfo>;
}

export function useSubscription(): SubscriptionState {
  const { user } = useAuth();
  const { offering, customerInfo, isLoading, setIsLoading } =
    useSubscriptionStore();

  const isPremium = computeIsPremium(rcPremiumActive(customerInfo), user?.accessLevel);

  useEffect(() => {
    const purchases = getServices().purchases;
    if (!user?.uid || !purchases.isConfigured()) {
      useSubscriptionStore.setState({ offering: null, customerInfo: null, isLoading: false });
      return;
    }
    // Store already hydrated (e.g. prefetch ran, or another consumer already fetched).
    if (offering != null && customerInfo != null) return;

    setIsLoading(true);
    prefetchSubscription(user.uid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const refreshEntitlements = useCallback(async (): Promise<PurchaseCustomerInfo | null> => {
    try {
      const info = await getServices().purchases.getCustomerInfo();
      onCustomerInfoUpdate(info);
      return info;
    } catch (err) {
      dev.warn('[useSubscription] refreshEntitlements failed:', err);
      return null;
    }
  }, []);

  const purchasePackage = useCallback(purchaseGated, []);
  const restorePurchases = useCallback(restoreGated, []);

  return {
    isPremium,
    isLoading,
    currentOffering: offering,
    customerInfo,
    managementURL: customerInfo?.managementURL ?? null,
    refreshEntitlements,
    purchasePackage,
    restorePurchases,
  };
}
